import { useRef, useEffect } from 'react';
import { FiGithub, FiExternalLink } from 'react-icons/fi';
import soundgraphImg from './images/soundgraph-play.png';
import techBlogImg from './images/tech-blog-image.png';
import arcadeImg from './images/Arcade-Lite-img.png';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import './Projects.css';

const PROJECTS = [
  {
    id: 'invest-bot',
    title: 'invest-bot',
    description:
      'Agentic paper-trading system with five specialized AI agents: research, sentiment analysis, congressional trading monitoring, signal synthesis, and trade execution — all orchestrated with CrewAI.',
    tags: ['Python', 'CrewAI', 'OpenAI', 'Redis', 'Docker', 'Alpaca'],
    github: 'https://github.com/Windowmac/invest-bot',
    live: null,
    gradient: 'linear-gradient(140deg, #071525 0%, #0e2a45 55%, #071525 100%)',
    icon: '🤖',
    featured: true,
  },
  {
    id: 'soundgraph-v2',
    title: 'Soundgraph V2',
    description:
      'Browser-based digital harmonica with true polyphonic playback via the Web Audio API. Supports pitch bending, multi-touch input, and four switchable tunings — zero native compilation required.',
    tags: ['React', 'TypeScript', 'Web Audio API'],
    github: 'https://github.com/Windowmac/Soundgraph_V2',
    live: null,
    gradient: 'linear-gradient(140deg, #130a2e 0%, #2e1260 55%, #130a2e 100%)',
    icon: '🎵',
    featured: true,
  },
  {
    id: 'snip',
    title: 'Snip',
    description:
      'Production-ready URL shortener demonstrating modern containerization: multi-stage Dockerfile, PostgreSQL persistence, Redis click-counter caching, and live analytics via Next.js Server Components.',
    tags: ['Next.js', 'TypeScript', 'Docker', 'PostgreSQL', 'Redis', 'Tailwind'],
    github: 'https://github.com/Windowmac/docker_experiment',
    live: null,
    gradient: 'linear-gradient(140deg, #060f22 0%, #0a2550 55%, #060f22 100%)',
    icon: '🔗',
    featured: true,
  },
  {
    id: 'soundgraph',
    title: 'Soundgraph',
    description:
      'Interactive browser sound generator with 3 octave ranges, touch & click playback, and a serverless MongoDB backend — the original exploration that led to V2.',
    tags: ['JavaScript', 'Web Audio API', 'MongoDB', 'Serverless'],
    github: 'https://github.com/Windowmac/soundgraph',
    live: 'https://windowmac-soundgraph.netlify.app/',
    image: soundgraphImg,
    featured: false,
  },
  {
    id: 'tech-blog',
    title: 'Tech Blog',
    description:
      'Full-stack blogging platform with user authentication, post & comment CRUD, cookie-based session management, and bcrypt password hashing — built on the MVC pattern.',
    tags: ['Node.js', 'Express', 'Sequelize', 'Handlebars', 'MySQL'],
    github: 'https://github.com/Windowmac/tech-blog',
    live: null,
    image: techBlogImg,
    featured: false,
  },
  {
    id: 'arcade-lite',
    title: 'Arcade-Lite',
    description:
      'Team-built Tetris game with a real-time leaderboard via Apollo/GraphQL, Context API state management, and MongoDB top-score persistence.',
    tags: ['React', 'GraphQL', 'Apollo', 'MongoDB', 'Context API'],
    github: 'https://github.com/Windowmac/arcade-lite',
    live: null,
    image: arcadeImg,
    featured: false,
  },
];

const ProjectCard = ({ project, index }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.transitionDelay = `${index * 0.08}s`;
          el.classList.add('revealed');
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [index]);

  return (
    <article
      ref={ref}
      className={`project-card reveal${project.featured ? ' project-card--featured' : ''}`}
    >
      <div
        className="project-card__visual"
        style={project.gradient ? { background: project.gradient } : undefined}
      >
        {project.image ? (
          <img src={project.image} alt={`${project.title} screenshot`} className="project-card__img" />
        ) : (
          <span className="project-card__icon" role="img" aria-label={project.title}>
            {project.icon}
          </span>
        )}
        {project.featured && (
          <span className="project-card__badge" aria-label="Featured project">Featured</span>
        )}
      </div>

      <div className="project-card__body">
        <h3 className="project-card__title">{project.title}</h3>
        <p className="project-card__desc">{project.description}</p>

        <div className="project-card__tags" aria-label="Technologies used">
          {project.tags.map((tag) => (
            <span key={tag} className="project-card__tag">{tag}</span>
          ))}
        </div>

        <div className="project-card__links">
          <a
            href={project.github}
            target="_blank"
            rel="noreferrer"
            className="project-card__link"
            aria-label={`${project.title} source code`}
          >
            <FiGithub aria-hidden="true" /> Code
          </a>
          {project.live && (
            <a
              href={project.live}
              target="_blank"
              rel="noreferrer"
              className="project-card__link project-card__link--live"
              aria-label={`${project.title} live demo`}
            >
              <FiExternalLink aria-hidden="true" /> Live
            </a>
          )}
        </div>
      </div>
    </article>
  );
};

const Projects = () => {
  const headerRef = useScrollReveal();

  return (
    <section id="projects" className="projects" aria-label="Projects">
      <div className="section-container">
        <div className="section-header reveal" ref={headerRef}>
          <p className="section-label">Work</p>
          <h2 className="section-title">Projects</h2>
        </div>

        <div className="projects__grid">
          {PROJECTS.map((project, i) => (
            <ProjectCard key={project.id} project={project} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
